import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  SectionOrderContextType, 
  SectionOrderPreferences,
  CollapsibleStatePreferences,
  DEFAULT_SECTION_ORDER,
  DEFAULT_COLUMN_ASSIGNMENT,
  DEFAULT_COLLAPSIBLE_STATES,
  createColumnAssignmentFromOrder
} from '../types/sectionOrder';

const SectionOrderContext = createContext<SectionOrderContextType | null>(null);

interface SectionOrderProviderProps {
  children: React.ReactNode;
  userId?: string;
  characterId?: string;
}

export const SectionOrderProvider: React.FC<SectionOrderProviderProps> = ({ children, userId, characterId }) => {
  const [sectionOrder, setSectionOrderState] = useState<string[]>(DEFAULT_SECTION_ORDER);
  const [columnAssignment, setColumnAssignmentState] = useState(DEFAULT_COLUMN_ASSIGNMENT);
  const [collapsibleStates, setCollapsibleStatesState] = useState<Record<string, boolean>>(DEFAULT_COLLAPSIBLE_STATES);
  const [isLoading, setIsLoading] = useState(true);

  // Load section order preferences from localStorage
  // Local storage provides optimal UX: device-specific layouts for mobile vs desktop contexts
  useEffect(() => {
    const loadSectionOrder = () => {
      try {
        if (userId) {
          const storedPreferences = localStorage.getItem(`sectionOrder_${userId}`);
          if (storedPreferences) {
            const preferences: SectionOrderPreferences = JSON.parse(storedPreferences);
            const sectionOrder = preferences.sectionOrder || DEFAULT_SECTION_ORDER;
            let columnAssignment = preferences.columnAssignment;
            
            // If no column assignment exists, create one from section order
            if (!columnAssignment) {
              columnAssignment = createColumnAssignmentFromOrder(sectionOrder);
            }
            
            setSectionOrderState(sectionOrder);
            setColumnAssignmentState(columnAssignment);
          } else {
            // Initialize with defaults including column assignment
            setSectionOrderState(DEFAULT_SECTION_ORDER);
            setColumnAssignmentState(createColumnAssignmentFromOrder(DEFAULT_SECTION_ORDER));
          }
        }
      } catch (error) {
        console.error('Failed to load section order preferences:', error);
        // Fall back to defaults on error
        setSectionOrderState(DEFAULT_SECTION_ORDER);
        setColumnAssignmentState(DEFAULT_COLUMN_ASSIGNMENT);
      } finally {
        setIsLoading(false);
      }
    };

    loadSectionOrder();
  }, [userId]);

  // Load collapsible state preferences from localStorage
  useEffect(() => {
    const loadCollapsibleStates = () => {
      try {
        const storedStates = localStorage.getItem('collapsibleStates');
        if (storedStates) {
          const preferences: CollapsibleStatePreferences = JSON.parse(storedStates);
          setCollapsibleStatesState(preferences.collapsibleStates || DEFAULT_COLLAPSIBLE_STATES);
        } else {
          // Initialize with defaults
          setCollapsibleStatesState(DEFAULT_COLLAPSIBLE_STATES);
        }
      } catch (error) {
        console.error('Failed to load collapsible state preferences:', error);
        // Fall back to defaults on error
        setCollapsibleStatesState(DEFAULT_COLLAPSIBLE_STATES);
      }
    };

    loadCollapsibleStates();
  }, []);

  // Save section order preferences to localStorage
  // Local storage is optimal: allows different layouts per device (mobile vs desktop contexts)
  const saveSectionOrder = useCallback(async (order: string[], assignment?: { left: string[]; right: string[] }) => {
    try {
      if (userId) {
        const preferences: SectionOrderPreferences = {
          userId,
          sectionOrder: order,
          columnAssignment: assignment || columnAssignment,
          lastModified: Date.now()
        };
        localStorage.setItem(`sectionOrder_${userId}`, JSON.stringify(preferences));
      }
    } catch (error) {
      console.error('Failed to save section order preferences:', error);
    }
  }, [userId, columnAssignment]);

  // Save collapsible state preferences to localStorage
  const saveCollapsibleStates = useCallback(async (states: Record<string, boolean>) => {
    try {
      const preferences: CollapsibleStatePreferences = {
        collapsibleStates: states,
        lastModified: Date.now()
      };
      localStorage.setItem('collapsibleStates', JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save collapsible state preferences:', error);
    }
  }, []);

  const setSectionOrder = useCallback((order: string[]) => {
    setSectionOrderState(order);
    saveSectionOrder(order);
  }, [saveSectionOrder]);

  const setColumnAssignment = useCallback((assignment: { left: string[]; right: string[] }) => {
    setColumnAssignmentState(assignment);
    saveSectionOrder(sectionOrder, assignment);
  }, [sectionOrder, saveSectionOrder]);

  const setSectionCollapsed = useCallback((sectionId: string, collapsed: boolean) => {
    const newStates = { ...collapsibleStates, [sectionId]: collapsed };
    setCollapsibleStatesState(newStates);
    saveCollapsibleStates(newStates);
  }, [collapsibleStates, saveCollapsibleStates]);

  const setAllSectionsCollapsed = useCallback((collapsed: boolean) => {
    const newStates = { ...collapsibleStates };
    
    // Update all known sections to the new collapsed state
    Object.keys(DEFAULT_COLLAPSIBLE_STATES).forEach(sectionId => {
      newStates[sectionId] = collapsed;
    });
    
    // Also update any additional sections that might exist
    Object.keys(collapsibleStates).forEach(sectionId => {
      newStates[sectionId] = collapsed;
    });
    
    setCollapsibleStatesState(newStates);
    saveCollapsibleStates(newStates);
  }, [collapsibleStates, saveCollapsibleStates]);

  const resetToDefault = useCallback(() => {
    setSectionOrderState(DEFAULT_SECTION_ORDER);
    setColumnAssignmentState(DEFAULT_COLUMN_ASSIGNMENT);
    setCollapsibleStatesState(DEFAULT_COLLAPSIBLE_STATES);
    saveSectionOrder(DEFAULT_SECTION_ORDER, DEFAULT_COLUMN_ASSIGNMENT);
    saveCollapsibleStates(DEFAULT_COLLAPSIBLE_STATES);
  }, [saveSectionOrder, saveCollapsibleStates]);

  const value: SectionOrderContextType = {
    sectionOrder,
    setSectionOrder,
    columnAssignment,
    setColumnAssignment,
    collapsibleStates,
    setSectionCollapsed,
    setAllSectionsCollapsed,
    resetToDefault,
    isLoading
  };

  return (
    <SectionOrderContext.Provider value={value}>
      {children}
    </SectionOrderContext.Provider>
  );
};

export const useSectionOrder = (): SectionOrderContextType => {
  const context = useContext(SectionOrderContext);
  if (!context) {
    throw new Error('useSectionOrder must be used within a SectionOrderProvider');
  }
  return context;
};